/**
 * Centralized program identifiers.
 *
 * The three core training programs (Mobility & Control, Strength in Range,
 * Functional Resilience) are referenced by:
 *   - Database UUIDs (user_programs, program_weeks, training_programs joins)
 *   - URL slugs (/program/mobility, /program/strength-in-range, …)
 *   - Internal logical keys ("foundation" / "strength" / "resilience")
 *
 * Every consumer was previously hardcoding these mappings independently
 * (WeeklyTrainingView, ProgramSession, useDashboardProgress, ProgramsSection).
 * Pulling them here means a future ID change is a single-file edit.
 */

export type ProgramSlug = "foundation" | "strength" | "resilience";

/** Program key → database UUID. Source of truth. */
export const PROGRAM_IDS: Record<ProgramSlug, string> = {
  foundation: "ec4bd3a3-804a-4fb9-bda8-c1d961e0c0e4",
  strength: "8e4b4627-aae8-4a30-be9a-af0d1e2de1b1",
  resilience: "8df0bb18-b3d7-4466-af4d-be4d711c7504",
} as const;

/** Program key → URL path segment used under /program/:routeSlug. */
export const ROUTE_SLUGS: Record<ProgramSlug, string> = {
  foundation: "mobility",
  strength: "strength-in-range",
  resilience: "functional-resilience",
} as const;

/** URL slug → internal program key. Used by ProgramSession route parser. */
export const ROUTE_SLUG_TO_KEY: Record<string, ProgramSlug> = {
  mobility: "foundation",
  "strength-in-range": "strength",
  "functional-resilience": "resilience",
};

/** UUID → URL slug. Used by dashboard progress when building resume routes. */
export const PROGRAM_ID_TO_ROUTE_SLUG: Record<string, string> = Object.fromEntries(
  (Object.keys(PROGRAM_IDS) as ProgramSlug[]).map((key) => [
    PROGRAM_IDS[key],
    ROUTE_SLUGS[key],
  ]),
);

/** UUID → URL slug, but lookups by route slug (legacy ProgramSession map). */
export const ROUTE_SLUG_TO_PROGRAM_ID: Record<string, string> = Object.fromEntries(
  (Object.keys(PROGRAM_IDS) as ProgramSlug[]).map((key) => [
    ROUTE_SLUGS[key],
    PROGRAM_IDS[key],
  ]),
);
